import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Modal, Spinner, ErrorBanner } from "../../../../../components/ui";
import { lotteryService } from "../../../../../services/api";
import styles from "./TicketModal.module.css";

/** Nombre de archivo seguro: ticket-<referencia o id>.png */
function ticketFileName(bet) {
  const raw = String(bet?.transaction_reference || bet?.id || "gold");
  const safe = raw.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
  return `ticket-${safe || "gold"}.png`;
}

/**
 * Muestra el ticket (PNG) de una apuesta. Se carga desde
 * GET /lottery/bets/{id}/ticket/ con auth y se presenta con un object URL
 * que se revoca al cerrar. Nada se envía automáticamente: el correo solo
 * sale si el usuario pulsa "Enviarme por correo".
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object|null} props.bet  Apuesta de GET /lottery/bets/ (se usa `bet.id`).
 * @param {() => void} props.onClose
 */
export default function TicketModal({ isOpen, bet, onClose }) {
  const betId = bet?.id ?? null;
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [imageUrl, setImageUrl] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { ok, message }
  const urlRef = useRef(null);

  const revokeUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen || betId == null) return undefined;
    let cancelled = false;

    setStatus("loading");
    setLoadError("");
    setImageUrl(null);
    revokeUrl();

    lotteryService.getBetTicket(betId).then((res) => {
      if (cancelled) return;
      if (res.ok && res.blob) {
        const url = URL.createObjectURL(res.blob);
        urlRef.current = url;
        setImageUrl(url);
        setStatus("ready");
      } else {
        setLoadError(res.error || "No pudimos cargar el ticket.");
        setStatus("error");
      }
    });

    return () => {
      cancelled = true;
      revokeUrl();
    };
  }, [isOpen, betId, reloadKey, revokeUrl]);

  // Al cerrar o cambiar de apuesta, limpiar mensajes del envío.
  useEffect(() => {
    if (!isOpen) {
      setSendResult(null);
      setSending(false);
      setImageUrl(null);
      setStatus("idle");
    }
  }, [isOpen]);

  useEffect(() => {
    setSendResult(null);
  }, [betId]);

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = ticketFileName(bet);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSendEmail = async () => {
    if (betId == null || sending) return;
    setSending(true);
    setSendResult(null);
    const res = await lotteryService.sendBetTicketEmail(betId);
    setSendResult({ ok: res.ok, message: res.message });
    setSending(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Tu ticket" size="md">
      <div className={styles.content}>
        {status === "loading" && (
          <div className={styles.loading}>
            <Spinner label="Cargando ticket…" />
          </div>
        )}

        {status === "error" && (
          <ErrorBanner
            message={loadError}
            onRetry={() => setReloadKey((k) => k + 1)}
          />
        )}

        {status === "ready" && imageUrl && (
          <>
            <div className={styles.ticketFrame}>
              <img
                src={imageUrl}
                alt={`Ticket de ${bet?.lottery_name || "tu apuesta"}, número ${
                  bet?.number ?? ""
                } serie ${bet?.series ?? ""}`}
                className={styles.ticketImg}
              />
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleDownload}
              >
                <FontAwesomeIcon icon={faDownload} />
                Descargar
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={handleSendEmail}
                disabled={sending}
                aria-busy={sending}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                {sending ? "Enviando…" : "Enviarme por correo"}
              </button>
            </div>

            {sendResult && (
              <p
                className={`${styles.sendMsg} ${
                  sendResult.ok ? styles.sendOk : styles.sendError
                }`}
                role={sendResult.ok ? "status" : "alert"}
              >
                {sendResult.message}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
